import { Box, Checkbox, Flex, Text } from "@chakra-ui/react";
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { ContactDto, UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { compareUserBodies } from "@bcasmart/doxecure-commons/doxecureFunctions";
import { ContactType, LockedAccount } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $count, $isemail, $length, $ok, $value } from "foundation-ts/commons";
import { $ascii, $trim } from "foundation-ts/strings";
import { UUID } from "foundation-ts/types";

import { ContactCard } from "@components/Cards/ContactCard";
import { SearchField } from "@components/SearchField/SearchField";
import { SectionBox } from "@components/SectionBox/SectionBox";
import useContacts from "@hooks/useContacts";
import { useStyles } from "@hooks/useStyles";
import { $g1Color } from "@/theme/G1Style";
import { SectionContacts } from "@utils/ReactTypesAndConstants";

interface ContactsListProps {
    connectedUserProfile: UserDto;
    blacklist?:Set<UUID>;
    contactType?: ContactType;
    ordered?: boolean;
    search?: boolean;
    lockedAccount?: LockedAccount;
    displayIcon?: boolean;
    selectable?: boolean;
    selectedIds?: Set<UUID>;
    onToggleSelect?: (item: ContactDto) => void;
    onToggleSelectAll?: (checked: boolean, items: ContactDto[]) => void;
    onViewItemClick?: (item: ContactDto) => void;
    onItemClick?: (item: ContactDto) => void;
    onRemoveItemClick?: (item: ContactDto) => void;
    onContactsLoaded?: (contacts: ContactDto[]) => void;
}

export const ContactsList = (props: ContactsListProps) => {
    const { 
        contactType, 
        connectedUserProfile, 
        blacklist,
        ordered,
        lockedAccount,
        search,
        displayIcon,
        selectable,
        selectedIds,
        onToggleSelect,
        onToggleSelectAll,
        onViewItemClick,
        onItemClick,
        onRemoveItemClick,
        onContactsLoaded
    } = props;
    const resolvedContactType = $value(contactType, ContactType.All);
    const { data: contacts } = useContacts(connectedUserProfile.apid!, resolvedContactType);
    const { sectionStyle, itemStyle } = useStyles();
    const [filteredContacts, setFilteredContacts] = useState<ContactDto[]>([]);
    const [contactsSections, setContactsSections] = useState<SectionContacts[]>([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const searchFormRef = useRef<HTMLFormElement>(null);
    const selectableContacts = useMemo(
        () => filteredContacts.filter(c => c.deletable ?? true),
        [filteredContacts]
    );

    const allSelected = selectable && selectableContacts.length > 0 && selectableContacts.every(c => selectedIds?.has(c.apid!));
    const someSelected = selectable && selectableContacts.some(c => selectedIds?.has(c.apid!));

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!!lockedAccount?.exceedsUsersLimit || !!lockedAccount?.canceledAndWaitingSessionEnds) { return; } // Nothing to do when account is blocked

        if ((e.type === "submit") && $ok(contacts)) {
            let contactsList: ContactDto[] = [];
            if ($length(searchValue.trim()) === 0) {
                contactsList = $ok(blacklist) ? contacts!.filter(c => $ok(c.contact.apid) && !blacklist!.has(c.contact.apid!)) : contacts!;
            } else {
                contactsList = filteredContacts!.filter(c => $isemail(searchValue.trim()) ? c.contact.email.toLowerCase() == searchValue.trim().toLowerCase() : c.contact.lastName.toLowerCase().includes(searchValue.trim().toLowerCase()));
            }
            setFilteredContacts(contactsList);
            setContactsSections($count(contactsList) ? createSectionList(contactsList).sort(compareSection) : []);
        }
    } ;

    const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    } ;

    const renderContactsBox = (item: ContactDto) => {
        const deletable = item.deletable ?? true;
        return (
            <ContactCard key={item.apid} item={item} itemStyle={itemStyle.contact} displayIcon={displayIcon}
                         selectable={selectable} selected={!!selectedIds?.has(item.apid!)}
                         onToggleSelect={onToggleSelect}
                         onViewItemClick={$ok(onViewItemClick) ? () => onViewItemClick!(item) : undefined}
                         onItemClick={$ok(onItemClick) ? () => onItemClick!(item) : undefined} 
                         onRemoveItemClick={deletable && $ok(onRemoveItemClick) ? () => onRemoveItemClick!(item) : undefined}
            />
        );
    };

    useEffect(() => {
        if (searchValue.length === 0) {
            searchFormRef.current?.requestSubmit();
        }
    }, [searchValue]);

    useEffect(() => {
        if ($ok(contacts)) {
            const contactsList = $ok(blacklist) ? contacts!.filter(c => $ok(c.contact.apid) && !blacklist!.has(c.contact.apid!)) : contacts!;
            setFilteredContacts(contactsList);
            setContactsSections($count(contactsList) ? createSectionList(contactsList).sort(compareSection) : []);
        }
    }, [contacts, blacklist]);

    useEffect(() => {
        if ($ok(contacts)) {
            onContactsLoaded?.(contacts!);
        }
    }, [contacts]);

    return (
        <>
            {search && (
                <form ref={searchFormRef} onSubmit={handleSubmit}>
                    <SearchField component="global" h="30px" mb="2%" placeHolder="Nom ou Adresse mail" onChange={handleSearchInputChange} />
                </form>
            )}
            {selectable && !!filteredContacts.length && (
                <Flex position="sticky" top={0} zIndex={1} alignItems="center" gap="2" py={2} bg={$g1Color('global.bg')}>
                    <Checkbox
                        isChecked={allSelected}
                        isIndeterminate={someSelected && !allSelected}
                        onChange={(e) => onToggleSelectAll?.(e.target.checked, selectableContacts)}
                    />
                    <Text fontSize="sm">Tout sélectionner</Text>
                </Flex>
            )}
            {!!contactsSections.length && (
                <>
                    {ordered ? (
                        contactsSections.map((section, index) => {
                            return (
                                <Box width="100%" key={index} mt={5}>
                                    <Text fontWeight="bold">{section.letter}</Text>
                                    <SectionBox items={section.items} sectionStyle={sectionStyle.contacts} renderItem={renderContactsBox} />
                                </Box>
                            );
                        })
                    ) : (
                        <SectionBox items={filteredContacts} sectionStyle={sectionStyle.contacts} renderItem={renderContactsBox} />
                    )}
                </>
            )}
        </>
    );
};

const compareUsers = (a: ContactDto, b: ContactDto) => {
    return compareUserBodies(a.contact, b.contact);
};

const compareSection = (a: SectionContacts, b: SectionContacts) => {
    if (a.letter < b.letter) {
        return -1;
    } else if (a.letter === b.letter) {
        return 0;
    } else {
        return 1;
    }
};

const createSectionList = (contacts:ContactDto[]): SectionContacts[] =>
{
    const sections:SectionContacts[] = [];
    for (let i = 0 ; i < 26 ; i++) {
        sections[i] = { items:[], letter:String.fromCharCode(65+i) };
    } 
    sections[26] = { items:[], letter:'?'};

    const sortedContacts = contacts.sort(compareUsers);
    sortedContacts.forEach(c => {
        const name = $trim(c.contact.lastName)+$trim(c.contact.firstName);
        if (name.length) {
            let index = $ascii(name.charAt(0)).toUpperCase().charCodeAt(0)-65;
            if (index < 0 || index >= 26) { index = 26; }
            sections[index].items.push(c);
        }
    }) ;
    return sections.filter(s => s.items.length > 0);
};